/**
 * Platform-wide Meta Pixel (browser) for the seller-acquisition funnel.
 *
 * This is the platform's OWN pixel — set by the super-admin — firing on the apex
 * marketing/auth/subscribe pages (homepage → pricing → register → subscribe →
 * paid). It is separate from each seller's storefront pixel (see MetaPixel).
 *
 * The pixel id is fetched once (public endpoint) and cached for the app session;
 * every funnel event routes through platformTrack so callers never need the id.
 */
import { initPixel, pixelTrack, type PixelUserData, type TrackOptions } from "./pixel";
import { getExternalId } from "./fbParams";
import { splitFullName } from "@shared/metaMatch";
import { resources } from "./resources";

type PixelEvent = Parameters<typeof pixelTrack>[1];

// undefined = not fetched yet; null = fetched, tracking off / no pixel.
let cached: string | null | undefined;
let inflight: Promise<string | null> | null = null;

/** Resolve (and cache) the platform pixel id, initialising the base pixel once. */
export async function ensurePlatformPixel(): Promise<string | null> {
  if (cached !== undefined) return cached;
  if (!inflight) {
    inflight = resources
      .platformPixel()
      .then((r) => r.pixelId ?? null)
      .catch(() => null)
      .then((id) => {
        cached = id;
        if (id) initPixel(id);
        return id;
      });
  }
  return inflight;
}

/**
 * The seller, in Meta's advanced-matching shape. Raw — fbevents.js hashes it.
 * The acquisition funnel only ever knows a name and an email, which is exactly
 * what the platform's server-side Purchase can match against later.
 */
export function sellerUserData(
  fullName?: string | null,
  email?: string | null,
): PixelUserData {
  const name = splitFullName(fullName);
  return { em: email, fn: name.first, ln: name.last, external_id: getExternalId() };
}

/** Fire a standard event against the platform pixel (no-op when tracking is off). */
export async function platformTrack(
  event: PixelEvent,
  params?: Record<string, unknown>,
  options?: TrackOptions,
): Promise<void> {
  const id = await ensurePlatformPixel();
  if (!id) return;
  pixelTrack(id, event, params, { ...options, capi: false });
}
